"use client";

import { useRouter } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import { Calendar, Clock, ArrowRight, Tag } from "lucide-react";
import BlogHeroSection from "../BlogHeroSection";
import Image from "next/image";

export default function BlogsList({ blogsList, meta, category, limit }) {
  const router = useRouter();
  const heroRef = useRef(null);
  const gridRef = useRef(null);

  const handleClick = (slug) => {
    router.push(`/blogs/${slug}`);
  };

  const tagStyles = [
    { color: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-500/10 dark:bg-emerald-950/50", border: "border-emerald-500/20" },
    { color: "text-cyan-600 dark:text-cyan-400", bg: "bg-cyan-500/10 dark:bg-cyan-950/50", border: "border-cyan-500/20" },
    { color: "text-lime-600 dark:text-lime-400", bg: "bg-lime-500/10 dark:bg-lime-950/50", border: "border-lime-500/20" },
    { color: "text-amber-600 dark:text-amber-400", bg: "bg-amber-500/10 dark:bg-amber-950/50", border: "border-amber-500/20" },
  ];

  const formatBlogs = (blogs = [], startIndex = 0) => {
    return blogs.map((blog, index) => {
      const globalIndex = startIndex + index;
      const style = tagStyles[globalIndex % tagStyles.length];
      return {
        ...blog,
        num: String(globalIndex + 1).padStart(2, "0"),
        tagColor: style.color,
        tagBg: style.bg,
        tagBorder: style.border,
        tag: blog.category || "General",
        read_time: blog.read_time || 5,
        created_at: new Date(blog.publishDate || blog.created_at).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        }),
        image: blog.featureImage || blog.image,
      };
    });
  };

  const formattedBlogs = useMemo(() => formatBlogs(blogsList || []), [blogsList]);

  const featuredBlogs = formattedBlogs.slice(0, 4);

  const [articles, setArticles] = useState(formattedBlogs.slice(4));
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(meta?.totalPages > 1);

  useEffect(() => {
    setArticles(formattedBlogs.slice(4));
    setPage(1);
    setHasMore(meta?.totalPages > 1);
  }, [formattedBlogs, meta]);

  const loadMore = async () => {
    if (loading || !hasMore) return;
    setLoading(true);
    try {
      const nextPage = page + 1;
      const res = await fetch(`${process.env.NEXT_PUBLIC_API_BASE_URL}/blog/getallposts/all?page=${nextPage}&limit=${limit}`);
      const data = await res.json();
      if (data?.data) {
        const currentCount = featuredBlogs.length + articles.length;
        const newFormatted = formatBlogs(data.data, currentCount);
        setArticles((prev) => {
          const existingSlugs = new Set(prev.map((a) => a.slug));
          const uniqueNew = newFormatted.filter((a) => !existingSlugs.has(a.slug));
          return [...prev, ...uniqueNew];
        });
        setPage(nextPage);
        if (nextPage >= data.meta.totalPages) {
          setHasMore(false);
        }
      }
    } catch (err) {
      console.error("Failed to load more blogs:", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    let ctx;
    const initGSAP = async () => {
      const { gsap } = await import("gsap");
      const { ScrollTrigger } = await import("gsap/ScrollTrigger");

      gsap.registerPlugin(ScrollTrigger);

      ctx = gsap.context(() => {
        if (heroRef.current) {
          const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

          tl.from(".hero-eyebrow", { y: 30, opacity: 0, duration: 0.8 })
            .from(".hero-line", { y: 80, opacity: 0, duration: 1.1, stagger: 0.12 }, "-=0.5")
            .from(".hero-sub", { y: 40, opacity: 0, duration: 1 }, "-=0.6");
        }

        if (gridRef.current) {
          const cards = gridRef.current.querySelectorAll(".blog-card");
          gsap.fromTo(
            cards,
            { opacity: 0, y: 60, scale: 0.96 },
            {
              opacity: 1,
              y: 0,
              scale: 1,
              duration: 0.75,
              ease: "power3.out",
              stagger: { amount: 0.45 },
              scrollTrigger: { trigger: gridRef.current, start: "top 80%" },
            },
          );
        }
      });
    };

    initGSAP().catch(console.warn);
    return () => ctx?.revert();
  }, []);

  return (
    <div className="bg-background-primary text-text-primary overflow-hidden">
      <BlogHeroSection />

      {featuredBlogs.length > 0 && (
        <section className="py-5! md:py-10! bg-background-primary">
          <div className="container mx-auto !px-6">
            <div className="flex justify-between items-center mb-10!">
              <h2 className="text-3xl lg:text-5xl font-semibold tracking-tight text-text-primary">Featured Stories</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 !gap-8">
              {featuredBlogs.map((blog, i) => (
                <article
                  key={i}
                  onClick={() => handleClick(blog.slug)}
                  className="group cursor-pointer border border-border-primary rounded-3xl overflow-hidden hover:border-cyan-500/30 transition-all duration-300"
                  style={{
                    background: "var(--card-background)",
                    boxShadow: "var(--card-shadow)",
                  }}
                >
                  <div className="h-60 overflow-hidden relative">
                    <Image src={blog.image} alt={blog.title} fill />
                  </div>

                  <div className="!p-6">
                    <div className="flex justify-between">
                      <div
                        className={`w-fit !mb-3 !px-4 !py-1.5 text-xs font-bold rounded-full flex items-center !gap-1.5 border ${blog.tagBorder || "border-transparent"} ${blog.tagBg} ${blog.tagColor}`}
                      >
                        <Tag size={12} />
                        {blog.tag}
                      </div>
                      <span className="text-2xl lg:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-b from-text-secondary/40 to-transparent">{blog.num}</span>
                    </div>

                    <h3 className="text-xl font-semibold leading-tight !mb-3 line-clamp-2 group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors">
                      {blog.title}
                    </h3>
                    <p className="text-secondary text-sm line-clamp-3 !mb-4">{blog.summary}</p>
                    <div className="flex  items-center justify-between gap-3 text-xs text-secondary border-t border-border-primary/20 !pt-3">
                      <div className="flex gap-2 ">
                        <Clock size={15} color="currentColor" /> {blog.read_time} min read
                      </div>
                      <div className="flex gap-1">
                        <Calendar size={15} color="currentColor" /> <span>{blog.created_at}</span>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="pb-14! bg-background-primary">
        <div className="container mx-auto !px-6">
          <h2 className=" !mb-10 border-b border-border-primary/20 pb-3! md:pb-5! text-3xl lg:text-5xl font-semibold tracking-tight text-text-primary  ">All Articles</h2>
          <div className="grid grid-cols-1 lg:grid-cols-12 !gap-10">
            <div className="lg:col-span-8  " ref={gridRef}>
              <div className="grid grid-cols-1 !gap-8 max-h-[810px] overflow-y-auto pr-4 custom-scrollbar modern-scroll">
                {articles.map((blog, i) => (
                  <article
                    key={i}
                    onClick={() => handleClick(blog.slug)}
                    className="blog-card group border border-border-primary rounded-3xl overflow-hidden cursor-pointer hover:border-cyan-500/30 transition-all duration-300 flex flex-col lg:flex-row"
                    style={{
                      background: "var(--card-background)",
                      boxShadow: "var(--card-shadow)",
                    }}
                  >
                    <div className="w-[25vw] overflow-hidden relative">
                      <Image src={blog.image} alt={blog.title} fill />
                    </div>

                    <div className="!p-6 w-full">
                      <div className="flex justify-between ">
                        <span
                          className={`w-fit !mb-5 top-4 left-4 !px-3 !py-1 text-[10px] font-bold rounded-full flex items-center gap-1 border ${blog.tagBorder || "border-transparent"} ${blog.tagBg} ${blog.tagColor}`}
                        >
                          <Tag size={10} />
                          {blog.tag}
                        </span>
                        <span className="text-2xl lg:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-b from-text-secondary/40 to-transparent">{blog.num}</span>
                      </div>
                      <h3 className="font-semibold text-lg leading-tight !mb-3 line-clamp-2 group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors">
                        {blog.title}
                      </h3>

                      <p className="text-secondary text-sm line-clamp-3 !mb-6">{blog.summary}</p>

                      <div className="flex items-center justify-between text-cyan-600 dark:text-cyan-400 font-medium text-sm">
                        <span className="flex items-center !gap-2 group-hover:gap-3 transition-all">
                          Read more <ArrowRight size={16} />
                        </span>
                      </div>
                      <div className="flex flex-col lg:flex-row items-start lg:items-center !gap-2 text-xs text-secondary !mb-3">
                        <span className="flex items-center !gap-1">
                          <Calendar size={13} color="currentColor" />
                          {blog.created_at}
                        </span>
                        <span>•</span>
                        <span className="flex items-center !gap-1">
                          <Clock size={13} color="currentColor" /> {blog.read_time} min read
                        </span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              {hasMore && (
                <div className="flex justify-center items-center !mt-16">
                  <button
                    onClick={loadMore}
                    disabled={loading}
                    className={`!px-6 !py-3 rounded-full text-sm font-medium flex items-center !gap-2 transition-all bg-gradient-to-br from-cyan-400 to-blue-500 text-white shadow-lg hover:shadow-cyan-500/30 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 cursor-pointer`}
                  >
                    {loading ? "Loading..." : "Load More"}
                  </button>
                </div>
              )}
            </div>

            <div className="lg:col-span-4 !space-y-8">
              <div
                className="border border-border-primary rounded-3xl !p-8"
                style={{
                  background: "var(--card-background)",
                  boxShadow: "var(--card-shadow)",
                }}
              >
                <h4 className="text-xl font-semibold !mb-6 border-b border-border-primary/20 !pb-5">Categories</h4>
                <div className="space-y-4!">
                  {category?.length > 0 ? (
                    category.map((cat, i) => (
                      <div key={i} className="flex justify-between items-center py-2 border-b border-border-primary/10 text-sm">
                        <span className="text-secondary">{cat.category}</span>
                        <span className="text-cyan-600 dark:text-cyan-400 font-medium">{cat.count}</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-secondary text-sm">No categories found</p>
                  )}
                </div>
              </div>

              <div
                className="border border-border-primary rounded-3xl !p-8"
                style={{
                  background: "var(--card-background)",
                  boxShadow: "var(--card-shadow)",
                }}
              >
                <h4 className="text-xl font-semibold !mb-6 border-b border-border-primary/20 !pb-5">Popular Posts</h4>
                <div className="!space-y-6">
                  {articles.slice(0, 4).map((blog, i) => (
                    <div key={i} onClick={() => handleClick(blog.slug)} className="flex gap-4 cursor-pointer group">
                      <div className="w-20 h-20 rounded-md overflow-hidden flex-shrink-0 relative">
                        <Image
                          src={blog.image}
                          alt={blog.title || "Blog image"}
                          fill
                          className="object-cover group-hover:scale-105 transition-transform duration-300"
                          sizes="80px"
                          priority={i === 0}
                        />
                      </div>
                      <div>
                        <p className="line-clamp-2 text-sm font-medium group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors">{blog.title}</p>
                        <p className="text-xs text-secondary !mt-2">{blog.created_at}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
